/**
 * Luggage stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_everyday_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const luggageStickersPack: ToilePack = {
    slug: 'luggage-stickers',
    category: 'sticker',
    name: 'Luggage stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'city', label: 'City label', subject: 'a round travel label of a skyline with a domed rooftop and the word "Lisbon" on a curved band'},
        {key: 'hotel', label: 'Hotel label', subject: 'an oval vintage hotel label with a palm tree, a seafront terrace and the name "Hotel Miramar"'},
        {key: 'voyage', label: 'Bon voyage', subject: 'a rectangular label with a small propeller plane over clouds and the words "Bon Voyage"'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco travel label, symmetrical geometric shapes, fine gold linework, smooth flat fills', palette: 'a palette of deep teal, gold, cream and coral', framing: 'Centered, with a clean white die-cut border and an even margin of background all round.'},
        {key: 'gouache', label: 'Gouache label', style: 'mid-century gouache travel label illustration, soft painted flat shapes with visible brush texture', palette: 'a palette of faded red, ochre, sea blue and cream', framing: 'Centered, with a scalloped paper edge and an even margin of background all round.'},
        {key: 'letterpress', label: 'Letterpress', style: 'vintage letterpress luggage label, two-ink print on aged paper with slight ink spread and worn corners', palette: 'a two-color palette of navy ink and red ink on cream paper', framing: 'Centered, with a worn paper edge and an even margin of background all round.'},
    ],
    directions: ['a ship', 'Paris', 'round shape'],
    directionHint: 'try: a ship',
    seo: {
        title: 'Luggage Stickers: City, Hotel and Bon Voyage Labels',
        metaDescription: 'Nine luggage sticker designs: a city label, a vintage hotel label and a bon voyage label, in art deco, gouache and letterpress styles.',
        h1: 'Luggage stickers: city label, hotel label, bon voyage label',
        tagline: 'Old-style travel labels for a suitcase, in three finishes.',
        intro: 'Travellers decorate suitcases with old-style travel labels that also make a bag easy to spot. These nine die-cut designs show a round Lisbon label with a domed skyline, an oval vintage hotel label with a palm tree and a seafront terrace, and a rectangular Bon Voyage label with a small plane over clouds. Each comes in art deco, mid-century gouache or letterpress style. You can also type a request, such as a ship, to shape the label.',
        useCases: [
            'a label on a suitcase handle',
            'a sticker for a travel journal',
            'a gift for someone who travels',
            'a bag that is easy to spot on a carousel',
        ],
        faq: [
            {q: 'What are the three styles?', a: 'Art deco uses teal, gold, cream and coral with fine gold lines, gouache uses faded red, ochre, sea blue and cream, and letterpress uses navy and red ink on aged cream paper.'},
            {q: 'Will the label stick to a suitcase?', a: 'The pictures show the label design on a plain background. How it sticks depends on the material you print it on.'},
            {q: 'Can I ask for a different place?', a: 'Yes, as a request, such as Paris. The nine labels show the three designs above.'},
        ],
    },
}
